import { useMemo } from 'react'
import type { AgentOutputProps } from '../types'
import { ExclamationTriangleIcon, InformationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline'

/**
 * QAReportRenderer — visual-qa, office-visual-qa.
 *
 * Expects { issues: [{ type, severity, page/slide, description, fix? }],
 * score?, first_pass_rate?, verdicts? }. Shows a header score badge + a
 * severity-ordered list of issue cards with the fix hint (if any).
 */

type Severity = 'critical' | 'major' | 'minor' | 'info'

interface Issue {
  type?: string
  severity?: Severity | string
  page?: number
  slide?: number
  description?: string
  fix?: string
}

interface Verdict {
  page?: number
  slide?: number
  status?: 'pass' | 'fail'
  issues?: Issue[]
}

interface QAReport {
  issues?: Issue[]
  verdicts?: Verdict[]
  score?: number
  first_pass_rate?: number
  max_iterations_reached?: boolean
}

const SEVERITY_META: Record<string, { color: string; bg: string; label: string; Icon: any }> = {
  critical: { color: '#DC2626', bg: '#DC262620', label: 'Critical', Icon: ExclamationTriangleIcon },
  major:    { color: '#EA580C', bg: '#EA580C20', label: 'Major',    Icon: ExclamationTriangleIcon },
  minor:    { color: '#CA8A04', bg: '#CA8A0420', label: 'Minor',    Icon: InformationCircleIcon  },
  info:     { color: '#2563EB', bg: '#2563EB20', label: 'Info',     Icon: InformationCircleIcon  },
}

function pickReport(result: any): QAReport | null {
  if (!result) return null
  const r = result
  const ad = r.agent_data || {}
  for (const src of [r, ad]) {
    if (Array.isArray(src?.issues) || Array.isArray(src?.verdicts) || typeof src?.score === 'number') {
      return src as QAReport
    }
  }
  return null
}

function flattenIssues(rep: QAReport): Array<Issue & { _loc: string }> {
  const out: Array<Issue & { _loc: string }> = []
  for (const iss of rep.issues || []) {
    const loc =
      typeof iss.page === 'number' ? `page ${iss.page}` :
      typeof iss.slide === 'number' ? `slide ${iss.slide}` : ''
    out.push({ ...iss, _loc: loc })
  }
  for (const v of rep.verdicts || []) {
    const loc = typeof v.page === 'number' ? `page ${v.page}` : typeof v.slide === 'number' ? `slide ${v.slide}` : ''
    for (const iss of v.issues || []) {
      out.push({ ...iss, _loc: iss.page || iss.slide ? `page ${iss.page ?? iss.slide}` : loc })
    }
  }
  return out
}

export default function QAReportRenderer(props: AgentOutputProps) {
  const rep = useMemo(() => pickReport(props.result), [props.result])
  const issues = useMemo(() => (rep ? flattenIssues(rep) : []), [rep])

  if (!rep) {
    return (
      <div className="text-sm text-muted-foreground italic">
        No QA report found in the agent result.
      </div>
    )
  }

  const passing = issues.length === 0
  return (
    <div className="space-y-3">
      {/* Header stats */}
      <div className="flex items-center gap-4 p-3 border border-border rounded-lg bg-muted/20">
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center shrink-0"
          style={{ background: passing ? '#10B98120' : '#EA580C20' }}
        >
          {passing ? (
            <CheckCircleIcon className="w-8 h-8" style={{ color: '#10B981' }} />
          ) : (
            <ExclamationTriangleIcon className="w-8 h-8" style={{ color: '#EA580C' }} />
          )}
        </div>
        <div className="flex-1">
          <div className="text-lg font-semibold text-foreground">
            {passing ? 'Visual QA: All checks passed' : `${issues.length} issue${issues.length === 1 ? '' : 's'} found`}
          </div>
          <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
            {typeof rep.score === 'number' && <span>score: <b className="text-foreground">{rep.score.toFixed(1)}/10</b></span>}
            {typeof rep.first_pass_rate === 'number' && (
              <span>first-pass: <b className="text-foreground">{(rep.first_pass_rate * 100).toFixed(0)}%</b></span>
            )}
            {rep.max_iterations_reached && <span className="text-destructive">max iterations reached</span>}
          </div>
        </div>
      </div>

      {/* Issue cards */}
      {issues.length > 0 && (
        <div className="space-y-2">
          {issues.map((iss, i) => {
            const sev = (iss.severity || 'info').toString().toLowerCase()
            const meta = SEVERITY_META[sev] ?? SEVERITY_META.info
            return (
              <div
                key={i}
                className="border border-border rounded-md p-3 bg-card"
                style={{ borderLeft: `3px solid ${meta.color}` }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className="text-xs font-bold px-2 py-0.5 rounded-full"
                    style={{ color: meta.color, background: meta.bg, border: `1px solid ${meta.color}40` }}
                  >
                    {meta.label}
                  </span>
                  {iss.type && <span className="text-xs text-muted-foreground font-mono">{iss.type}</span>}
                  {iss._loc && <span className="text-xs text-muted-foreground ml-auto">{iss._loc}</span>}
                </div>
                {iss.description && (
                  <div className="text-sm text-foreground">{iss.description}</div>
                )}
                {iss.fix && (
                  <div className="mt-2 text-xs text-muted-foreground bg-muted/40 border-l-2 border-primary pl-2 py-1">
                    <span className="font-semibold text-primary">Fix:</span> {iss.fix}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
